import { Injectable } from '@angular/core';
import { Sync } from '@shared/models/grid';

@Injectable()
export class SyncService {
  /**
   * @description Merges multiple sync models into one sync model.
   */
  mergeModels(syncModels: Sync<any>[]): Sync<any> {
    const result = this.getDefaultSyncModel();

    syncModels.forEach((item) => {
      if (
        !item.addedList?.length &&
        !item.updatedList?.length &&
        !item.deletedIds?.length
      ) {
        return;
      }

      result.addedList.push(...(item.addedList ?? []));
      result.updatedList.push(...(item.updatedList ?? []));
      result.deletedIds.push(...(item.deletedIds ?? []));
    });

    // this re-assignation is needed to avoid having duplicate items in deletedIds
    result.deletedIds = Array.from(new Set([...result.deletedIds]));

    return result;
  }

  /**
   * @description Sanitize the added and updated values based on a given sanitize function
   */
  sanitizeModel<T, R>(
    syncModel: Sync<T>,
    sanitizeFunction: (x: T) => R
  ): Sync<R> {
    if (!syncModel) {
      return null;
    }

    const sanitizedSyncModel = this.getDefaultSyncModel<R>();

    if (syncModel.addedList) {
      syncModel.addedList.forEach((added) => {
        sanitizedSyncModel.addedList.push(sanitizeFunction(added));
      });
    }

    if (syncModel.updatedList) {
      syncModel.updatedList.forEach((updated) => {
        sanitizedSyncModel.updatedList.push(sanitizeFunction(updated));
      });
    }

    if (syncModel.deletedIds) {
      sanitizedSyncModel.deletedIds.push(...syncModel.deletedIds);
    }

    return sanitizedSyncModel;
  }

  getDefaultSyncModel<T>(): Sync<T> {
    return { addedList: [], updatedList: [], deletedIds: [] };
  }

  isSyncModelEmpty<T>(sync: Sync<T>): boolean {
    return (
      !!sync.addedList?.length &&
      !!sync.updatedList?.length &&
      !!sync.deletedIds?.length
    );
  }
}
